import {
  ReactNode, useEffect, useState, useRef, useMemo,
} from 'react';
import styled, { FlattenInterpolation, ThemeProps } from 'styled-components';
import { Input, InputCss, InputFocus } from './Input';
import { OutsideClicker } from '../../OutsideClicker';
import { ThemeType } from '../../../styling';
import { useKeyDown } from '../../../util/useKeyDown';
import { Tooltip } from '../../Tooltip';
import { useIsXOverflow } from '../../../util';
import { useFormConfigurationContext } from '../../../..';

export const SelectInputIcon = styled.i<{ small: boolean, disabled?: boolean, iconCss?: FlattenInterpolation<ThemeProps<ThemeType>> | string }>` ${({
  theme, small, disabled,
}) => `
  color: ${disabled ? theme.colours.neutral[8] : theme.textNeutralDark}; 
  position: absolute;
  font-size: ${theme.fontSize[3]};
  top: 50%;
  transform: translateY(-50%);
  right: ${(() => {
    if (small) return theme.padding[2];
    return theme.padding[4];
  })()};
  cursor: pointer;
  pointer-events: none;
`};
${({ iconCss }) => iconCss};
`;

const SelectContainer = styled.div`
  position: relative;
  width: 100%;
`;

export const SelectInputField = styled.div.attrs<{disabled?: boolean}>(({ disabled = false }) => ({
  ...(disabled ? {} : { tabIndex: '0' }),
}))<{
  small?: boolean,
  styles?: string,
  disabled?: boolean,
  hasError?: boolean,
  value: string | number,
  dropdownCss?: FlattenInterpolation<ThemeProps<ThemeType>> | string
}>`
  ${InputCss};
  ${({
    theme, styles, disabled, value, hasError,
  }) => `
    ${styles}
    width: 100%;
    appearance: none;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: ${theme.padding[2]};
    background: ${theme.colours.neutral[10]};
    color: ${theme.colours.neutral[2]};
    border: ${theme.borderWidth[1]} solid ${hasError ? theme.textDanger : theme.colours.neutral[6]};
    cursor: ${(disabled) ? 'context-menu' : 'pointer'};
    ${(disabled) && `
      background: ${theme.colours.neutral[8]};
      border: none;
    `};

    ${(!disabled) && `
      &:hover {
        box-shadow: 0px ${theme.borderWidth[1]} ${theme.borderWidth[2]} rgba(16, 24, 40, 0.05);
        border-radius: ${theme.borderRadius[1]};
      }
      &:focus {        
        box-shadow: 0px 0px 0px ${theme.padding[1]} ${hasError ? theme.danger : theme.colours.info[1]};
        background-color: ${theme.colours.neutral[10]};
        border-color: ${hasError ? theme.textDanger : theme.colours.primary[2]};
      }
    `};
    ${(value === '') && `
      &:first-child{
        color:  ${theme.colours.neutral[5]};
      }
      &:focus {
        box-shadow: 0px 0px 0px 0px;
      }
    `};

  `};
  ${({ style }) => `${style}`};
  ${({ dropdownCss }) => dropdownCss};
  ${InputFocus}
`;

export const SelectInputDropdown = styled.div`${({ theme }) => `
  position: absolute;
  z-index: 2;
  width: 100%;
  overflow-x: unset !important;
  overflow-y: auto !important;
  max-height: calc(${theme.padding[12]} + ${theme.padding[10]});
  padding: ${theme.padding[1]} 0;
  box-shadow: 0px 0px 4px rgba(0, 0, 0, 0.16);
  margin-top: ${theme.padding[1]};
  background-color: ${theme.colours.neutral[10]};
  border: 0 solid ${theme.colours.neutral[10]};
  border-radius: ${theme.borderRadius[1]};
  color: ${theme.colours.neutral[1]};
  font-size: ${theme.fontSize[1]};
  font-weight: ${theme.fontWeight.normal};
`}`;

export const DropdownItem = styled.div<{ checked?: boolean, focused: boolean }>`${({ theme, checked, focused }) => `
  padding: ${theme.padding[2]} ${theme.padding[4]};
  line-height: ${theme.fontSize[3]};
  display: flex;
  align-items: center;
  font-size: ${theme.fontSize[1]};

  &:hover {
    cursor: pointer;
    background-color: ${theme.colours.secondary[6]};
  }
  ${checked && `
    background-color: ${theme.colours.secondary[4]};
  `};
  ${focused && `
    background: ${theme.colours.secondary[5]};
    font-weight: ${theme.fontWeight.bold};
  `};
  white-space: normal;
`}`;

const Placeholder = styled.div`${({ theme }) => `
  color: ${theme.colours.neutral[5]};
`}`;

const SelectedValue = styled.div`
  overflow: hidden; 
  white-space: nowrap; 
  text-overflow: ellipsis;
  padding-right: ${({ theme }) => theme.padding[5]};
`;

export type SelectInputFieldProps = {
  id: string,
  label?: ReactNode,
  value: string | number,
  onChange: (v: string) => void,
  options: { [key: string]: string },
  labelStyles?: string,
  disabled?: boolean,
  touchOverride?: boolean,
  onBlur?: (targetValue?: string) => void,
  placeholder?: ReactNode,
  required?: boolean,
  small?: boolean,
  visible?: boolean,
  inline?: boolean,
  dropdownCss?: FlattenInterpolation<ThemeProps<ThemeType>> | string,
  iconCss?: FlattenInterpolation<ThemeProps<ThemeType>> | string,
  isTooltipError?: boolean,
  styles?: string,
  errors?: string[];
  warnings?: string[];
  showValueTooltip?: 'onHover' | 'onTruncateHover';
}

export const SelectFieldInput = styled.select`
${InputCss};
${({
    theme, disabled, value, hasError,
  }) => `
  margin-right: ${theme.padding[2]};
  color: ${theme.textPrimary};
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: ${theme.padding[2]};
  padding-left: ${theme.padding[1]};
  background: ${disabled ? theme.neutralDark : theme.textSecondary};
  border: ${theme.borderWidth[1]} solid ${hasError ? theme.textDanger : theme.borderDark};
  ${!disabled && `
    &:hover {
      cursor: pointer;
      background-color: ${theme.neutral};      
    }
    &:focus {
      box-shadow: 0px 0px 0px ${theme.borderWidth[2]} ${hasError ? theme.danger : theme.sidebarHighlight};
    }
  `}
  ${value === '' && `
    &:first-child{
      color:  ${theme.textNeutral};
    }
  `}
  option {
    color: ${theme.textPrimary};
    font-weight:${theme.fontWeight.normal};
  }
  width:100%; 
  overflow:hidden; 
  white-space:nowrap; 
  text-overflow:ellipsis;
`}
${({ style }) => `${style}`}
`;

export const StyledSelect = styled(SelectFieldInput) <{ styles?: string }>`
  ${({ style }) => `${style}`}
`;

export function SelectInputValue({
  id,
  showValueTooltip,
  children,
  tooltipContent,
}: {
  id: string,
  showValueTooltip: SelectInputFieldProps['showValueTooltip'],
  children: ReactNode,
  tooltipContent?: string
}) {
  const ref = useRef<HTMLDivElement>(null);
  const isTruncated = useIsXOverflow(ref);

  const hasTooltip = useMemo(() => {
    if (!showValueTooltip) return false;
    if (showValueTooltip === 'onHover') return true;
    return isTruncated;
  }, [showValueTooltip, isTruncated]);

  return (
    <>
      <SelectedValue ref={ref}>
        {children}
      </SelectedValue>
      {tooltipContent && hasTooltip && (
        <Tooltip
          id={id}
          place="top"
          html={tooltipContent}
        />
      )}
    </>
  );
}

export function SelectInput({
  id,
  label,
  value = '',
  onChange,
  options,
  labelStyles,
  disabled,
  touchOverride = false,
  placeholder,
  required = false,
  small = false,
  visible = true,
  onBlur,
  inline = false,
  dropdownCss,
  iconCss,
  isTooltipError = false,
  styles = '',
  errors = [],
  warnings = [],
  showValueTooltip,
}: SelectInputFieldProps) {
  const [touched, setTouched] = useState(false);
  const ref = useRef<HTMLInputElement>(null);
  const selectContainerRef = useRef<HTMLDivElement>(null);
  const hasError = (touched || touchOverride) && !!errors?.length;
  const [isOpen, setIsOpen] = useState(false);
  const [dropdownFocused, setDropdownFocused] = useState(false);

  const { isDisabled } = useFormConfigurationContext(disabled);

  const onToggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const onItemClick = (targetValue: string) => {
    onChange(targetValue);
    onBlur?.(targetValue);
    setIsOpen(false);
  };

  const onClickOutside = () => {
    setIsOpen(false);
    onBlur?.(value.toString());
  };

  const checkSelected = (optionValue: string) => (optionValue === value?.toString?.());

  useEffect(() => setTouched(false), [visible]);

  const [focusedItemIndex, setFocusedItemIndex] = useState<number>(-1);

  const optionsArray = Object.entries(options);

  useKeyDown(() => {
    if (isOpen) {
      setFocusedItemIndex((prev) => (prev === optionsArray.length - 1 ? 0 : prev + 1));
    }
  }, ['ArrowDown'], isOpen);

  useKeyDown(() => {
    if (isOpen) {
      setFocusedItemIndex((prev) => (prev === 0 ? optionsArray.length - 1 : prev - 1));
    }
  }, ['ArrowUp'], isOpen);

  useKeyDown(() => {
    if (isOpen && focusedItemIndex >= 0) {
      onItemClick(optionsArray[focusedItemIndex][0]);
    }
  }, ['Enter', ' '], isOpen);

  useKeyDown(() => {
    if (dropdownFocused) {
      onToggleDropdown();
    }
  }, ['Enter', ' '], dropdownFocused);

  useKeyDown(() => {
    if (isOpen) {
      onToggleDropdown();
    }
  }, ['Tab', 'Escape'], false);

  useEffect(() => {
    if (isOpen) {
      setFocusedItemIndex(0);
    }
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && focusedItemIndex >= 0 && selectContainerRef?.current) {
      const element = selectContainerRef?.current.getElementsByClassName('select-options')?.[focusedItemIndex];
      element?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
    }
  }, [focusedItemIndex]);

  return (
    <Input
      id={id}
      label={label}
      hasError={hasError}
      errors={errors}
      warnings={warnings}
      labelStyles={labelStyles}
      required={required}
      inline={inline}
      disabled={isDisabled}
      isTooltipError={isTooltipError}
      hideErrorMessage={isOpen}
    >
      <SelectContainer ref={selectContainerRef}>
        <SelectInputField
          id={`select-input-value-${id}`}
          disabled={isDisabled}
          hasError={hasError}
          onClick={isDisabled ? () => { } : onToggleDropdown}
          dropdownCss={dropdownCss}
          ref={ref}
          value={value}
          styles={styles}
          small={small}
          onFocus={() => setDropdownFocused(true)}
          onBlur={() => { setTouched(true); setDropdownFocused(false); }}
        >
          <SelectInputValue
            id={`select-input-value-${id}`}
            showValueTooltip={showValueTooltip}
            tooltipContent={options[value]}
          >
            {options[value] || ((isDisabled && value === '') ? '' : <Placeholder>{placeholder ?? 'Please select'}</Placeholder>)}
          </SelectInputValue>
        </SelectInputField>
        <SelectInputIcon
          className={(isOpen) ? 'mi-chevron-up' : 'mi-chevron-down'}
          small={small}
          iconCss={iconCss}
          disabled={isDisabled}
        />
        {(isOpen && !isDisabled) && (
          <OutsideClicker
            onClickOutside={onClickOutside}
            ignoredEffectElements={[ref.current]}
          >
            <SelectInputDropdown id={`${id}-select`}>
              <>
                {optionsArray.map(
                  ([key, optionValue], index) => (
                    <DropdownItem
                      id={`${id}-select-${key}`}
                      className="select-options"
                      onClick={() => onItemClick(key)}
                      key={key}
                      checked={checkSelected(key)}
                      focused={focusedItemIndex === index}
                    >
                      {optionValue}
                    </DropdownItem>
                  ),
                )}
              </>
            </SelectInputDropdown>
          </OutsideClicker>
        )}
      </SelectContainer>
    </Input>
  );
}
